import cl from 'classnames';
import { noop } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { responsiveBreakPoints } from '../../../../../../hooks/useDeviceQuery.hook';
import { Typography } from '../../../../../DesignSystemV2/Typography';
import { Nothing } from '../../../../shared-components/Nothing';
import { useExerciseQuizDoTestStore } from '../../store/exercise-quiz-do-test.store';
import {
  DoTestExerciseQuizProps,
  DoTestQuizAnswerModeEnum,
  ExerciseQuizSupportTypeEnum,
} from '../../types';
import { OptionAudio } from './Option/OptionAudio';
import { OptionImage } from './Option/OptionImage';
import { OptionText } from './Option/OptionText';

const Main = styled.div`
  .question-title {
    text-align: center;
  }

  .question-option-list {
    margin: 32px 0;
    display: grid;

    &.${ExerciseQuizSupportTypeEnum.IMAGE}, &.${ExerciseQuizSupportTypeEnum.AUDIO} {
      grid-auto-flow: row;
      gap: 16px;
      @media ${responsiveBreakPoints['above-540']} {
        grid-auto-flow: column;
        grid-auto-columns: 1fr;
      }
    }

    &.${ExerciseQuizSupportTypeEnum.TEXT} {
      gap: 12px;
    }
  }
`;

const getAnswerMode = ({
  submitted,
  correctAnswerId,
  optionId,
  isUserAnswer,
}): DoTestQuizAnswerModeEnum => {
  if (!submitted) {
    return DoTestQuizAnswerModeEnum.DO_TEST;
  }

  if (optionId === correctAnswerId) return DoTestQuizAnswerModeEnum.RESULT_CORRECT;
  if (isUserAnswer && optionId !== correctAnswerId) return DoTestQuizAnswerModeEnum.RESULT_WRONG;

  return DoTestQuizAnswerModeEnum.RESULT_DEFAULT;
};

export const DoTestExerciseQuizQuestion = observer((props: DoTestExerciseQuizProps) => {
  const { children, attributes, element } = props;
  const store = useExerciseQuizDoTestStore();
  const userAnswer = store.userAnswers.get(element.id);
  const quizIndex = store.quizIds.findIndex((it) => it === element.id);
  if (quizIndex !== store.currentQuizIndex) {
    return <Nothing {...props} />;
  }

  return (
    <Main className="do-test-exercise-quiz-question" contentEditable={false} {...attributes}>
      <div
        onMouseDown={(e) => e.preventDefault()}
        contentEditable={false}
        className="question-title"
      >
        <Typography.Text variant="semi-bold/24">{children}</Typography.Text>
      </div>

      <div contentEditable={false} className={cl('question-option-list', element.quizType)}>
        {element.options.map((option, optionIndex) => {
          const { id } = option;
          let percentage = 0;
          const questionStatistic = store.statistics.get(element.id);
          if (questionStatistic) {
            const currentStatistic = questionStatistic.answers.get(id);
            percentage = Math.round(
              ((currentStatistic?.noOfSelections || 0) /
                Math.max(questionStatistic.countTotalAnswers, 1)) *
                100
            );
          }
          const answerMode = getAnswerMode({
            optionId: id,
            isUserAnswer: userAnswer?.answerId === id,
            correctAnswerId: element.correctAnswer,
            submitted: userAnswer?.submitted,
          });
          const onUserAnswer = () => {
            store.answer(element.id, id);
          };

          const defaultProps = {
            key: id,
            mode: answerMode,
            selected: userAnswer?.answerId === id,
            onClick: userAnswer?.submitted ? noop : onUserAnswer,
            percentage,
          };
          switch (element.quizType) {
            case ExerciseQuizSupportTypeEnum.IMAGE:
              return (
                <OptionImage {...defaultProps} text={option.imageText} image={option.imageFile} />
              );
            case ExerciseQuizSupportTypeEnum.AUDIO: {
              return (
                <OptionAudio
                  {...defaultProps}
                  optionIndex={optionIndex}
                  text={option.audioText}
                  audio={option.audioFile}
                  audioType={option.audioType}
                />
              );
            }
            case ExerciseQuizSupportTypeEnum.TEXT:
            default:
          }
          return <OptionText key={id} {...defaultProps} text={option.text} />;
        })}
      </div>
    </Main>
  );
});
